import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaBan, FaFlag, FaImage, FaFile } from 'react-icons/fa';
import supabase from '../../../supabaseClient';
import './ContactInfoView.css';

const ContactInfoView = ({
  contact,
  enrichedData,
  onBack,
  conversationId,
  userId,
}) => {
  const [activeTab, setActiveTab] = useState('info');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [blockStatus, setBlockStatus] = useState({ blocked: false, blockedBy: false });

  const displayName = enrichedData?.display_name || enrichedData?.name || contact?.name || contact?.email || 'Contact';
  const avatarUrl = enrichedData?.avatar_url || contact?.avatar_url || '/avatar-placeholder.png';
  const bio = enrichedData?.bio || '';
  const location = enrichedData?.location || '';
  const nativeLanguages = enrichedData?.native_languages || [];
  const targetLanguages = enrichedData?.target_languages || [];
  const isGroup = contact?.type === 'group' || contact?.type === 'academy_group';
  const profileLink = isGroup ? `/app/groups/${contact?.id}` : `/app/profile/${contact?.id}`;

  // Check block status
  useEffect(() => {
    if (!userId || !contact?.id || isGroup) return;

    const checkBlockStatus = async () => {
      const { data: blockedByMe } = await supabase
        .from('blocked_users')
        .select('id')
        .eq('user_id', userId)
        .eq('blocked_user_id', contact.id)
        .maybeSingle();

      const { data: blockedByThem } = await supabase
        .from('blocked_users')
        .select('id')
        .eq('user_id', contact.id)
        .eq('blocked_user_id', userId)
        .maybeSingle();

      setBlockStatus({
        blocked: !!blockedByMe,
        blockedBy: !!blockedByThem,
      });
    };

    checkBlockStatus();
  }, [userId, contact?.id, isGroup]);

  // Fetch media and documents when tab changes
  useEffect(() => {
    if (activeTab !== 'media' || !contact?.id || !userId) return;

    const fetchAttachments = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('messages')
          .select('id, media_url, content, created_at, sender_id')
          .not('media_url', 'is', null)
          .order('created_at', { ascending: false })
          .limit(50);

        if (isGroup) {
          query = query.eq('conversation_id', contact.id);
        } else {
          query = query.or(
            `and(sender_id.eq.${userId},receiver_id.eq.${contact.id}),and(sender_id.eq.${contact.id},receiver_id.eq.${userId})`
          );
        }

        const { data, error } = await query;

        if (error) {
          console.error('[ContactInfoView] Error fetching attachments:', error);
          setMediaFiles([]);
          setDocuments([]);
          return;
        }

        // Filter: only show files from attachments/ subfolder (not composer messages)
        const attachments = (data || []).filter((msg) => {
          const url = msg.media_url || '';
          // Only include files uploaded via file picker (stored in attachments/ subfolder)
          return url.includes('/attachments/');
        });

        // Classify into media vs documents
        const imageExts = /\.(png|jpg|jpeg|gif|webp|bmp|svg)$/i;
        const isGiphy = (url) => url.includes('giphy.com');
        const docExts = /\.(pdf|doc|docx|ppt|pptx|xls|xlsx|txt|csv|zip|rar)$/i;

        const media = [];
        const docs = [];

        attachments.forEach((msg) => {
          const url = msg.media_url;
          if (imageExts.test(url) || isGiphy(url)) {
            media.push(msg);
          } else if (docExts.test(url)) {
            docs.push(msg);
          } else {
            // Other files (including attached audio/video files) go to docs
            docs.push(msg);
          }
        });

        setMediaFiles(media);
        setDocuments(docs);
      } catch (err) {
        console.error('[ContactInfoView] Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttachments();
  }, [activeTab, contact?.id, userId, isGroup]);

  const handleBlock = async () => {
    if (!userId || !contact?.id || isGroup) return;

    if (blockStatus.blocked) {
      // Unblock
      const { error } = await supabase
        .from('blocked_users')
        .delete()
        .eq('user_id', userId)
        .eq('blocked_user_id', contact.id);

      if (!error) {
        setBlockStatus((prev) => ({ ...prev, blocked: false }));
      }
    } else {
      // Block
      const { error } = await supabase
        .from('blocked_users')
        .insert({ user_id: userId, blocked_user_id: contact.id });

      if (!error) {
        setBlockStatus((prev) => ({ ...prev, blocked: true }));
      }
    }
  };

  const handleReport = () => {
    // TODO: Open report modal
    window.dispatchEvent(
      new CustomEvent('virtulingu:openReportModal', {
        detail: { userId: contact?.id, displayName, context: 'messenger' },
      })
    );
  };

  const getFilename = (url) => {
    try {
      const parts = url.split('/');
      const filename = parts[parts.length - 1].split('?')[0];
      // Remove UUID prefix if present
      const match = filename.match(/^[a-f0-9-]{36}\.(.+)$/i);
      return match ? match[1] : filename;
    } catch {
      return 'File';
    }
  };

  return (
    <div className="contact-info-view">
      {/* Header with back button above title */}
      <div className="contact-info-header">
        <button className="contact-info-back-btn" onClick={onBack} title="Back to chat">
          <FaArrowLeft />
        </button>
        <h3>Contact Info</h3>
      </div>

      {/* Profile summary */}
      <div className="contact-info-profile">
        <img src={avatarUrl} alt={displayName} className="contact-info-avatar" />
        <h2 className="contact-info-name">{displayName}</h2>
        {bio && <p className="contact-info-bio">{bio}</p>}
        {location && (
          <p className="contact-info-location">
            <span>📍</span> {location}
          </p>
        )}
        {nativeLanguages.length > 0 && (
          <p className="contact-info-languages">
            <span>🗣️ Native:</span> {nativeLanguages.join(', ')}
          </p>
        )}
        {targetLanguages.length > 0 && (
          <p className="contact-info-languages">
            <span>🎯 Learning:</span> {targetLanguages.join(', ')}
          </p>
        )}
      </div>

      {/* Primary Action */}
      <div className="contact-info-actions">
        <Link to={profileLink} className="action-btn primary" target="_blank" rel="noopener noreferrer">
          <FaUser />
          <span>View Profile</span>
        </Link>
      </div>

      {/* Block status banner */}
      {blockStatus.blockedBy && (
        <div className="block-banner">This user has restricted messages from you.</div>
      )}

      {/* Tabs */}
      <div className="contact-info-tabs">
        <button
          className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          Info
        </button>
        <button
          className={`tab-btn ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Media & Files
        </button>
      </div>

      {/* Tab content */}
      <div className="contact-info-content">
        {activeTab === 'info' && (
          <div className="info-tab">
            {isGroup ? (
              <p className="info-description">
                {enrichedData?.description || 'No group description available.'}
              </p>
            ) : (
              <p className="info-description">
                {bio || 'No bio available.'}
              </p>
            )}
          </div>
        )}

        {activeTab === 'media' && (
          <div className="media-tab">
            {loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <>
                {/* Media grid */}
                {mediaFiles.length > 0 && (
                  <div className="media-section">
                    <h4>
                      <FaImage /> Media ({mediaFiles.length})
                    </h4>
                    <div className="media-grid">
                      {mediaFiles.map((msg) => (
                        <a
                          key={msg.id}
                          href={msg.media_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="media-item"
                        >
                          <img src={msg.media_url} alt="" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* Files list */}
                {documents.length > 0 && (
                  <div className="files-section">
                    <h4>
                      <FaFile /> Files ({documents.length})
                    </h4>
                    <ul className="files-list">
                      {documents.map((msg) => (
                        <li key={msg.id} className="file-item">
                          <FaFile className="file-icon" />
                          <a
                            href={msg.media_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="file-link"
                          >
                            {msg.content || getFilename(msg.media_url)}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {mediaFiles.length === 0 && documents.length === 0 && (
                  <div className="empty-state">No media or files shared yet.</div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Safety Actions - at the very bottom */}
      {!isGroup && (
        <div className="contact-info-safety-section">
          <button className="safety-action-btn" onClick={handleReport}>
            <FaFlag />
            <span>Report</span>
          </button>
          <button
            className={`safety-action-btn ${blockStatus.blocked ? 'unblock' : 'danger'}`}
            onClick={handleBlock}
          >
            <FaBan />
            <span>{blockStatus.blocked ? 'Unblock' : 'Block'}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default ContactInfoView;
